import React, { useState, useEffect, useContext } from "react";
import ErrorMessage from "@/components/elements/global/ErrorMessage";
import { SchedulePushNotificationContext } from "@/context/SchedulePushNotificationContext";
import { BuildPath } from "@/helpers/Paths";
import { ScheduleType } from "@/interfaces/ScheduledNotifications";
import useHTTP from "@/components/hooks/useHTTP";
import { getContentIdFromContentUrl } from "@/components/elements/search/utility";

const ReviewStep = ({ setFormValid, publishDetails }: {setFormValid: any, publishDetails: any}) => {
  const [displayErrorMessage, setDisplayErrorMessage] = useState(false);
  const [isDateInvalid, setIsDateInvalid] = useState<boolean>(false);

  //variables for view
  const [detailType, setDetailType] = useState<string>("");
  const [detailDate, setDetailDate] = useState<string>("");
  const [detailTime, setDetailTime] = useState<string>("");
  const [detailReach, setDetailReach] = useState<string>("");

  //state management
  const { store, setStore } = useContext(SchedulePushNotificationContext);
  const [isStoreDataLoaded, setIsStoreDataLoaded] = useState<boolean>(false);
  const { get } = useHTTP();

  //variables for date logic
  const currentDate = new Date().toISOString();
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "short",
      year: "numeric",
    };
    return new Intl.DateTimeFormat(undefined, options).format(date);
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      hour: "numeric",
      minute: "numeric",
    };
    return new Intl.DateTimeFormat(undefined, options).format(date);
  };

  function setTime(publishDate: string) {
    const formattedPublishDate = formatDate(publishDate)
    const formattedPublishTime = formatTime(publishDate)
    setDetailDate(formattedPublishDate)
    setDetailTime(formattedPublishTime)
}

  //ensuring the state is loaded before loading screen
  useEffect(() => {
    if (!isStoreDataLoaded && store.scheduledPushTime) {
      setIsStoreDataLoaded(true);
    }
  }, []);

  // on component load get latest scheduled push details
  useEffect(() => {
    get(`notifications/content/${getContentIdFromContentUrl(window.location.href)}/scheduledpushdetails`)
    .then(({data}) => {
      setDetailReach(data.reach)
      setStore({
        ...store,
        reach: data.reach,
      });
    })
  },[])

  useEffect(() => {
    //setting notification type view
    if (store.notificationType == 0) {
      setDetailType("Quiet Push");
    } else {
      setDetailType("Quiet + Standard Push");
    }
    //setting date and time view
    if (store.scheduledPushTime && store.scheduleType) {
      if (store.scheduleType === ScheduleType.NOW) {
        const dateFormat = formatDate(currentDate);
        setDetailDate(dateFormat);
        setDetailTime("Now");
      } else if (store.scheduleType === ScheduleType.PUBLISH 
        ||
        new Date (store.scheduledPushTime).getTime() === new Date(publishDetails.scheduledPushTime).getTime()) {
        setTime(publishDetails.scheduledPublishDate);
      } else {
        setTime(store.scheduledPushTime);
      }
    } else {
      setDisplayErrorMessage(true);
    }

    //setting reach view
    if (publishDetails.reach != null) {
      setDetailReach(publishDetails.reach);
      setStore({
        ...store,
        reach: publishDetails.reach,
      });
    } else {
      setDetailReach("-");
    }
    setIsStoreDataLoaded(true);
  }, [isStoreDataLoaded]);

  useEffect(() => {
    if (
      store && 
      (store.scheduleType != ScheduleType.NOW)
    ) {
      let notificationDate : Date;
      if (store.scheduleType === ScheduleType.CHOOSE){
        notificationDate = new Date(store.scheduledPushTime);
      } else {
        notificationDate = new Date(publishDetails.scheduledPublishDate);
      }

      const intervalId = setInterval(() => {
        const currentDate = new Date();
        if (currentDate.getTime() > notificationDate.getTime()) {
          setIsDateInvalid(true);
          setFormValid(false);
        } else {
          setIsDateInvalid(false);
          setFormValid(true);
        }
      }, 1000);

      // Cleanup interval on component unmount
      return () => {
        clearInterval(intervalId);
      }
    }
    setFormValid(true);
  }, []);

  return (
    <>
      {displayErrorMessage && (
        <ErrorMessage
          className={"something-went-wrong-error"}
          errorMessage={
            "Something went wrong. Revisit previous steps and try again."
          }
        />
      )}

      {isDateInvalid && (
        <ErrorMessage
          className={"something-went-wrong-error"}
          errorMessage={"You must select a time and date in the future"}
        />
      )}

      <div
        className="notification-review-step"
        data-testid="notification-review-step"
      >
        <div className="notification-detail-containers">
          <div className="detail-containers">
            <span title="notification-type-detail" className="detail-type">
              Notification Type
            </span>

            <span className="detail">{detailType}</span>
          </div>

          <div className="detail-containers">
            <span className="detail-type">Date</span>
            <span className="detail">{detailDate}</span>
          </div>

          <div className="detail-containers">
            <span className="detail-type">Time</span>
            <span className="detail">{detailTime}</span>
          </div>

          <div className="detail-containers">
            <span className="detail-type">Reach</span>
            <span className="detail">{detailReach}</span>
          </div>
        </div>

        <div className="notification-type-preview">
          <div className="preview">
            <img
              className="preview-image app-preview"
              data-testid="app-preview-image"
              src={`${BuildPath()}/images/schedulepush/in-app-preview.png`}
              alt=""
            />
            <p>Quiet Push</p>
          </div>

          {store.notificationType === 1 && (
            <div className="preview device-preview">
              <img
                className="preview-image"
                data-testid="device-preview-image"
                src={`${BuildPath()}/images/schedulepush/device-push-preview.png`}
                alt=""
              />
              <p>+ Standard Push</p>
            </div>
          )}
        </div>

        <div className="notification-type-truncation-warning">
          Notification text will be truncated differently on different devices.
        </div>
      </div>
    </>
  );
};

export default ReviewStep;
